const assetRegistry = window.themeDeferredAssetRegistry || {
  styles: new Map(),
  scripts: new Map(),
};

window.themeDeferredAssetRegistry = assetRegistry;

function findStylesheet(href) {
  const resolvedHref = new URL(href, document.baseURI).href;
  return Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
    .find((link) => link.href === resolvedHref);
}

function loadStyle(href) {
  if (!href) return Promise.resolve();
  href = new URL(href, document.baseURI).href;
  if (assetRegistry.styles.has(href)) return assetRegistry.styles.get(href);

  const existing = findStylesheet(href);
  if (existing?.sheet) {
    const ready = Promise.resolve(existing);
    assetRegistry.styles.set(href, ready);
    return ready;
  }

  const request = new Promise((resolve, reject) => {
    const link = existing || document.createElement('link');
    const cleanup = () => {
      link.removeEventListener('load', onLoad);
      link.removeEventListener('error', onError);
    };
    const onLoad = () => {
      cleanup();
      resolve(link);
    };
    const onError = () => {
      cleanup();
      assetRegistry.styles.delete(href);
      link.remove();
      reject(new Error(`Unable to load stylesheet: ${href}`));
    };
    link.addEventListener('load', onLoad);
    link.addEventListener('error', onError);
    if (!existing) {
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
    }
  });
  assetRegistry.styles.set(href, request);
  return request;
}

function loadScript(src) {
  if (!src) return Promise.resolve();
  src = new URL(src, document.baseURI).href;
  if (assetRegistry.scripts.has(src)) return assetRegistry.scripts.get(src);

  const request = import(/* @vite-ignore */ src);
  assetRegistry.scripts.set(src, request);
  return request;
}

if (!customElements.get('deferred-assets')) {
  customElements.define('deferred-assets', class DeferredAssets extends HTMLElement {
    connectedCallback() {
      this.connectionVersion = (this.connectionVersion || 0) + 1;
      if (this.observer || this.dataset.assetsLoaded === 'true') return;

      const section = this.closest('.shopify-section');
      let hashId = window.location.hash.slice(1);
      try { hashId = decodeURIComponent(hashId); } catch { /* A malformed hash must not block component loading. */ }
      const hashTarget = hashId ? document.getElementById(hashId) : null;
      const shouldLoadImmediately = Boolean(
        this.hasAttribute('data-immediate') ||
        window.Shopify?.designMode ||
        (hashTarget && section?.contains(hashTarget))
      );

      if (shouldLoadImmediately || !('IntersectionObserver' in window)) {
        this.load();
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.disconnectObserver();
        this.load();
      }, {
        rootMargin: this.dataset.rootMargin || '800px 0px',
      });
      this.observer.observe(this.closest('.shopify-section') || this);
    }

    disconnectedCallback() {
      this.connectionVersion += 1;
      this.loadRequest = null;
      this.disconnectObserver();
    }

    disconnectObserver() {
      if (!this.observer) return;
      this.observer.disconnect();
      this.observer = null;
    }

    load() {
      if (!this.isConnected || this.dataset.assetsLoaded === 'true') return;
      if (this.loadRequest) return this.loadRequest;
      const version = this.connectionVersion;
      const isCurrent = () => this.isConnected && this.connectionVersion === version;

      const request = loadStyle(this.dataset.css)
        .then(() => {
          if (!isCurrent()) return;
          const selector = this.dataset.templateSelector;
          const template = selector ? this.closest('.shopify-section')?.querySelector(selector) : null;
          if (template instanceof HTMLTemplateElement) {
            template.replaceWith(template.content.cloneNode(true));
          }
          return loadScript(this.dataset.js);
        })
        .then(() => {
          if (!isCurrent()) return;
          this.dataset.assetsLoaded = 'true';
          this.dispatchEvent(new CustomEvent('theme:assets:ready', { bubbles: true }));
        })
        .catch((error) => {
          if (!isCurrent()) return;
          this.dataset.assetsLoaded = 'false';
          console.warn('Unable to load component assets', error);
        })
        .finally(() => {
          if (this.loadRequest === request) this.loadRequest = null;
        });
      this.loadRequest = request;
      return request;
    }
  });
}
